import {
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Image,
  Link,
  List,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Check } from 'lucide-react'
import automotiveImage from '~/assets/illustrations/automotiveIllustrationMedium.jpeg'
import { cardHover, PageHeader, SectionHeading } from '~/components/shell'

export const Route = createFileRoute('/_app/automotive/')({ component: AutomotiveDemo })

function AutomotiveDemo() {
  const navigate = useNavigate()

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader title="Automotive" subtitle="Connected vehicles and fleet management" />

      <VStack align="stretch" gap={10}>
        {/* Customer story */}
        <Card.Root overflow="hidden" boxShadow="2xl">
          <Stack direction={{ base: 'column', md: 'row' }}>
            <Image
              objectFit="cover"
              maxW={{ base: '100%', md: '300px' }}
              src={automotiveImage}
              alt="Automotive"
            />
            <Stack flex={1} mb="6">
              <Card.Header>
                <Heading size="lg" color="gray.600">
                  Accelerating Revenue with Connected Vehicle Innovation
                </Heading>
              </Card.Header>
              <Card.Body>
                <VStack align="flex-start" gap={4}>
                  <Text>
                    Conn Car Company recognized that connected vehicles represent a $100B+ market
                    opportunity - but legacy platforms couldn't scale to handle 25GB of data per
                    vehicle annually, intermittent connectivity, and massive rush-hour load spikes
                    without degrading customer experience.
                  </Text>
                  <Text>
                    By implementing the{' '}
                    <Link
                      href="https://www.hivemq.com/products/hivemq-platform/"
                      target="_blank"
                      rel="noreferrer"
                      color="blue.500"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      HiveMQ Platform
                    </Link>
                    , Conn Car built a connected vehicle infrastructure that transforms raw vehicle
                    data into new revenue streams while delivering the seamless experience customers
                    demand.
                  </Text>
                  <Text>
                    HiveMQ's enterprise-grade MQTT platform handles millions of concurrent vehicle
                    connections with sub-second latency - even during peak traffic - enabling
                    real-time services that differentiate Conn Car in a competitive market.
                  </Text>
                  <Text fontWeight="medium">Business Impact Delivered:</Text>
                  <List.Root pl={4}>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>New subscription revenue</b> from premium connected services including
                          remote diagnostics and personalized experiences
                        </Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>30% reduction in warranty costs</b> through predictive maintenance that
                          catches issues before they become failures
                        </Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>99.99% platform availability</b> ensuring reliable service delivery
                          across millions of vehicles globally
                        </Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>Faster time-to-market</b> for new connected features with a scalable,
                          future-proof data platform
                        </Text>
                      </HStack>
                    </List.Item>
                  </List.Root>
                </VStack>
              </Card.Body>
            </Stack>
          </Stack>
        </Card.Root>

        {/* Platform journey */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="The Industrial Data Platform for Agentic AI" />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Connect
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Millions of vehicles streaming real-time telemetry from edge to cloud with MQTT
                </Text>
              </Card.Body>
            </Card.Root>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Contextualize
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Raw sensor data transformed into actionable fleet insights and driver profiles
                </Text>
              </Card.Body>
            </Card.Root>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Analyze
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Fleet performance, usage patterns, and component health monitored in real time
                </Text>
              </Card.Body>
            </Card.Root>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Act
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Predictive maintenance signals trigger proactive service before failures occur
                </Text>
              </Card.Body>
            </Card.Root>
          </SimpleGrid>
        </VStack>

        {/* Data flow, UNS and AI */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="Data Flow, UNS and AI" />
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} gap={6}>
            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/automotive/flow' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">Data Flow</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Explore the automotive data flow architecture.
                </Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/automotive/flow' })
                  }}
                >
                  View Flow
                </Button>
              </Card.Footer>
            </Card.Root>

            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/automotive/explorer' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">UNS Explorer</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">Explore the Unified Namespace in real-time.</Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/automotive/explorer' })
                  }}
                >
                  Explore UNS
                </Button>
              </Card.Footer>
            </Card.Root>

            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/automotive/bot' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">AI Assistant</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">Chat with AI about automotive data.</Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/automotive/bot' })
                  }}
                >
                  Chat with AI
                </Button>
              </Card.Footer>
            </Card.Root>

            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/automotive/knowledge-graph' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">Knowledge Graph</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Visualize the automotive UNS as a real-time network graph.
                </Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/automotive/knowledge-graph' })
                  }}
                >
                  View Graph
                </Button>
              </Card.Footer>
            </Card.Root>
          </SimpleGrid>
        </VStack>
      </VStack>
    </Box>
  )
}
